import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'
import { CircleAlert } from 'lucide-react'
import { DataTable } from '@/components/ui/data-table'
import { Badge } from '@/components/ui/badge'
import { NameCell } from './TableCells'
import { statMetricColumns } from './statsColumns'
import { useStatLabel } from '../hooks/useStatLabel'
import {
  TARGETING_STAT_METRICS,
  useTargetingStats,
  type TargetingTotalsRow,
} from '../hooks/useTargetingStats'
import type { ExpressionType, StatsQuery } from '@/services/stats/statsApi'
import type { DateRange } from '@/services/stats/statsRange'

/**
 * Targeting types name the same things the targeting editor does, so the type
 * column reuses those labels rather than showing the ad server's enum name.
 */
export const TYPE_LABEL_KEYS: Record<ExpressionType, string> = {
  AD_UNIT: 'targeting.types.adUnit.plural',
  AD_UNIT_MATCHING_LABEL: 'targeting.types.adUnitMatchingLabel.plural',
  ARTICLE: 'targeting.types.url.plural',
  CATEGORY: 'targeting.types.category.plural',
  DATE: 'targeting.types.date.plural',
  DAY_PARTING: 'targeting.types.dayParting.plural',
  DEVICE_PROPERTY: 'targeting.types.device.plural',
  DOMAIN_NAME: 'targeting.types.domain.plural',
  FIRST_PARTY_AUDIENCE: 'targeting.types.firstPartyAudience.plural',
  GEOSPATIAL: 'targeting.types.geospatial.plural',
  IP_ADDRESS: 'targeting.types.ipAddress.plural',
  KEYWORD: 'targeting.types.keyword.plural',
  KEY_VALUE: 'targeting.types.keyValue.plural',
  NAMED_LOCATION: 'targeting.types.namedLocation.plural',
  ORGANISATION: 'targeting.types.organisation.plural',
  PUBLISHER: 'targeting.types.publisher.plural',
  RETARGETING: 'targeting.types.retargeting.plural',
  SEMANTIC_CATEGORY: 'targeting.types.semantic.plural',
  SIMILAR_KEYWORD: 'targeting.types.similarKeyword.plural',
  SITE: 'targeting.types.site.plural',
  SITE_COUNTRY: 'targeting.types.siteCountry.plural',
  SITE_GROUP: 'targeting.types.siteGroup.plural',
  THIRD_PARTY_AUDIENCE: 'targeting.types.thirdPartyAudience.plural',
  UNRECOGNISED: 'targeting.types.unrecognised.plural',
  USER_SEGMENT: 'targeting.types.userSegment.plural',
  WEATHER: 'targeting.types.weather.plural',
}

/**
 * The targeting type as a compact pill. Badges keep the column scannable, so
 * this trims their padding and lets a long label ellipsize inside the narrow
 * column rather than widening it — the full name is in the title.
 */
export function TypeBadge({ type }: { type: ExpressionType }) {
  const { t } = useTranslation()
  const label = t(TYPE_LABEL_KEYS[type] ?? type)
  return (
    <Badge variant="outline" className="max-w-full px-1.5 font-normal" title={label}>
      <span className="truncate">{label}</span>
    </Badge>
  )
}

/** What every breakdown table in the card is handed. */
export interface BreakdownTableProps {
  /** Which stats to break down — e.g. `{ lineItemId }`. */
  query: Partial<StatsQuery>
  range: DateRange
  /** Caps the rows' height in px so the card does not outgrow the page. */
  maxHeight?: number
}

/** Delivery per targeting value the ad server matched, all types in one list. */
export function TargetingTotalsTable({ query, range, maxHeight }: BreakdownTableProps) {
  const { t } = useTranslation()
  const { rows, isLoading, error } = useTargetingStats(query, range)
  const statLabel = useStatLabel()

  const columns = useMemo(
    (): ColumnDef<TargetingTotalsRow, unknown>[] => [
      {
        id: 'type',
        header: t('stats.targetingType'),
        size: 96,
        meta: { label: t('stats.targetingType') },
        accessorFn: (row) => t(TYPE_LABEL_KEYS[row.type] ?? row.type),
        cell: ({ row }) => <TypeBadge type={row.original.type} />,
      },
      {
        id: 'value',
        header: t('stats.targetingValue'),
        size: 260,
        meta: { label: t('stats.targetingValue') },
        accessorFn: (row) => row.value,
        cell: ({ row }) => <NameCell name={row.original.value} />,
      },
      ...statMetricColumns<TargetingTotalsRow>({
        metrics: TARGETING_STAT_METRICS,
        statsFor: (row) => row.stats,
        label: statLabel,
      }),
    ],
    [t, statLabel]
  )

  if (error) {
    return (
      <div className="flex items-center gap-2 px-(--card-spacing) py-4 text-destructive">
        <CircleAlert className="size-4" />
        <span className="text-sm">{error.message}</span>
      </div>
    )
  }

  return (
    <DataTable
      columns={columns}
      data={rows}
      loading={isLoading}
      getRowId={(row) => `${row.type}=${row.value}`}
      emptyMessage={t('stats.noTargetingTotals')}
      columnsKey="targetingTotals"
      maxHeight={maxHeight}
      inset
    />
  )
}
